import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { LOGO } from "@/data/exercises";
import { GardienBadge } from "@/components/GardienBadge";

const FIELDS = [
  ["code", "Code d'invitation", "text", "Ex. K7M2Q9XP"],
  ["name", "Votre nom", "text", "Ex. Marie Dupont"],
  ["email", "Votre e-mail", "email", "marie@exemple.fr"],
  ["password", "Choisissez un mot de passe", "password", "8 caractères minimum"],
];

export default function JoinFamily() {
  const { joinFamily } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ code: "", name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await joinFamily(form);
      navigate("/nouvelles");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-8 bg-accent/30 font-care">
      <form onSubmit={submit} className="w-full max-w-md bg-card rounded-3xl border border-border/60 shadow-[0_20px_40px_rgb(0,0,0,0.05)] p-8 sm:p-10" data-testid="join-family-form">
        <img src={LOGO} alt="Passerelle" className="h-16 w-auto mb-5 mx-auto" />
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground text-center">Nouvelles de mon proche</p>
        <h1 className="font-serif-display text-3xl text-center mt-1 mb-3">Rejoindre votre proche</h1>
        <p className="text-center text-foreground/70 mb-6">Votre proche vit en établissement ? Saisissez le code remis par l'équipe soignante pour suivre ses nouvelles. Cet accès est gratuit.</p>
        <GardienBadge id="rose-auguste" eyebrow="Gardiens du lien familial" className="mb-6" testId="join-gardien" />
        {error && <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="join-error">{error}</div>}
        <div className="space-y-4">
          {FIELDS.map(([k, label, type, ph]) => (
            <div key={k}>
              <Label htmlFor={`join-${k}`}>{label}</Label>
              <Input id={`join-${k}`} type={type} required value={form[k]} placeholder={ph}
                minLength={k === "password" ? 8 : undefined}
                onChange={(e) => setForm({ ...form, [k]: k === "code" ? e.target.value.toUpperCase() : e.target.value })}
                className={`mt-1 h-12 ${k === "code" ? "tracking-[0.3em] font-semibold uppercase" : ""}`} data-testid={`join-${k}-input`} />
            </div>
          ))}
        </div>
        <Button type="submit" disabled={loading} className="w-full h-12 mt-6 text-base text-white rounded-full border-0" style={{ background: "#2E8EC2" }} data-testid="join-submit-button">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Créer mon accès famille"}
        </Button>
        <p className="text-center text-sm text-gray-500 mt-5">
          Déjà un compte ? <Link to="/connexion" className="text-emerald-700 font-semibold" data-testid="join-login-link">Se connecter</Link>
        </p>
      </form>
    </div>
  );
}
